import { CommonModule, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslationService } from '../../../services/translation.service';
import { ModelMetricDto } from '../../../interfaces';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

export interface ClaimFlagIcon {
  src: string;
  tooltip: string;
}
export interface SystemResultConfigI {
  label: string;
  subtitle?: string;
  viewDetailsLabel?: string;
  viewDetailsKey?: string;
  data: any;
  type?:
    | 'NUMERIC'
    | 'PERCENTAGE'
    | 'STRING'
    | 'BOOLEAN'
    | 'LEVEL_BADGE'
    | 'BOOLEAN_BADGE'
    | 'INFO_BADGE';
  severity?: 'LowSeverity' | 'MediumSeverity' | 'HighSeverity' | string;
  modelMetrics?: ModelMetricDto;
  subcategory?: {
    label?: string;
    value?: number | string;
    badge?: {
      data?: any;
      type?: 'LEVEL_BADGE' | 'BOOLEAN_BADGE' | 'INFO_BADGE';
      tooltip?: string;
    };
  };
}

@Component({
  selector: 'app-system-result-card',
  standalone: true,
  imports: [
    NgClass,
    TranslatePipe,
    CommonModule,
    PopoverModule,
    TooltipModule,
  ],
  templateUrl: './system-result-card.component.html',
  styleUrl: './system-result-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemResultCardComponent implements OnInit {
  data: InputSignal<SystemResultConfigI[]> = input.required();
  claimFlagIcons: InputSignal<ClaimFlagIcon[]> = input<ClaimFlagIcon[]>([]);
  viewDetails: OutputEmitterRef<SystemResultConfigI> = output();
  private translateService = inject(TranslationService);

  ngOnInit(): void {
    this.data().map((item) => {
      if (item.type == 'PERCENTAGE') {
        item.data = item.data * 100;
      }
      return item;
    });
  }

  openDetails(metric: SystemResultConfigI, event: Event): void {
    // event.stopPropagation();
    this.viewDetails.emit(metric);
  }

  // getSystemResultIcon(systemResult: string) {
  //   if (systemResult.toLowerCase() == 'suspicious') {
  //     return 'text-xl fa fa-exclamation-triangle mr-3 ml-5 text-white bg-danger';
  //   } else if (
  //     systemResult.toLowerCase() == 'notsuspicious' ||
  //     systemResult.toLowerCase() == 'not suspicious'
  //   ) {
  //     return 'text-xl fa-sharp fa-solid fa-square-check mr-3 ml-5 text-white bg-success';
  //   }
  //   return '';
  // }

  getBackgroundColor(metric: SystemResultConfigI) {
    if (this.isSeverityMetric(metric)) {
      return this.getSeverityColor(metric.severity);
    }
    if (metric.type == 'PERCENTAGE') {
      return this.getRuleBasedProfileProperties(metric.data).colorClass;
    } else if (metric.type == 'STRING') {
      return this.getAnomalyDetectionBackgroundColor(metric.data);
    } else if (metric.type == 'BOOLEAN') {
      if (metric.data == 0) {
        // 0 is not clear
        return 'bg-warning';
      } else if (metric.data == -1) {
        // -1 is outlier
        return 'bg-danger';
      } else if (metric.data == 1) {
        // 1 is regular
        return 'bg-success';
      }
    } else {
      return this.getRuleBasedProfileProperties(metric.data).colorClass;
    }
    return '';
  }
  
  getIcon(metric: SystemResultConfigI) {
    if (!metric.data && !this.isSeverityMetric(metric)) {
      return 'text-3xl fa-solid fa-ban text-white';
    }
    if (this.isSeverityMetric(metric)) {
      return this.getSeverityIcon(metric.severity);
    }
    if (metric.type == 'PERCENTAGE') {
      return this.getRuleBasedProfileProperties(metric.data).iconClass;
    } else if (metric.type == 'STRING') {
      if (metric.data.toLowerCase() == 'regular') {
        return 'text-3xl fa-sharp fa-solid fa-check text-white';
      } else if (
        metric.data.toLowerCase() == 'extreme outlier' ||
        metric.data.toLowerCase() == 'outlier'
      ) {
        return 'text-3xl fa fa-exclamation-triangle text-white';
      }
    } else if (metric.type == 'BOOLEAN') {
      if (metric.data == 0) {
        return 'text-3xl fa fa-exclamation-triangle text-white';
      } else if (metric.data == -1) {
        return 'text-3xl fa fa-exclamation-triangle text-white';
      } else if (metric.data == 1) {
        return 'text-3xl fa-sharp fa-solid fa-check text-white';
      }
    } else {
      return this.getRuleBasedProfileProperties(metric.data).iconClass;
    }
    return;
  }

  private getAnomalyDetectionBackgroundColor(status: string) {
    let color = 'bg-secondary';

    if (!status) {
      return;
    }

    if (status.toLowerCase() === 'regular') {
      color = 'bg-success ';
    } else if (status.toLowerCase() === 'outlier') {
      color = 'bg-warning';
    } else if (status.toLowerCase() === 'extreme outlier') {
      color = 'bg-danger';
    }
    return color;
  }

  getTextSize() {
    let size = 'font-bold fs-6';

    return size;
  }

  // getDataFormat(data: SystemResultConfigI) {
  //   if (!data.data) {
  //     return 'No available data';
  //   }
  //   if (data.type == 'BOOLEAN') {
  //     if (data.data == 1) {
  //       return 'Regular';
  //     } else if (data.data == 0) {
  //       // TO BE HANDLED
  //       return 'Regular';
  //     } else if (data.data == -1) {
  //       return 'Outlier';
  //     }
  //   } else if (data.type == 'PERCENTAGE' || data.type == 'NUMERIC') {
  //     return `${data.data.toFixed(2)}%`;
  //   } else if (data.type == 'STRING') {
  //     return data.data;
  //   }
  // }

  getDataFormat(metric: SystemResultConfigI): string {
    if (metric.type === 'LEVEL_BADGE') {
      return '';
    }

    if (
      metric.data === null ||
      metric.data === undefined ||
      metric.data === ''
    ) {
      return this.translateService.getTranslation(
        'CASE_DETAIL.NO_AVAILABLE_DATA'
      );
    }

    if (metric.type === 'BOOLEAN') {
      const num = Number(metric.data);
      if (num === 1)
        return this.translateService.getTranslation('CASE_DETAIL.Regular');
      if (num === 0)
        return this.translateService.getTranslation('CASE_DETAIL.Regular');
      if (num === -1)
        return this.translateService.getTranslation('CASE_DETAIL.Outlier');
      return this.translateService.getTranslation(
        'CASE_DETAIL.NO_AVAILABLE_DATA'
      );
    }

    if (metric.type === 'PERCENTAGE') {
      const num = Number(metric.data);
      return Number.isFinite(num)
        ? `${num.toFixed(2)}%`
        : this.translateService.getTranslation('CASE_DETAIL.NO_AVAILABLE_DATA');
    }

    if (metric.type === 'NUMERIC') {
      const num = Number(metric.data);
      return Number.isFinite(num)
        ? num.toFixed(0)
        : this.translateService.getTranslation('CASE_DETAIL.NO_AVAILABLE_DATA');
    }

    if (metric.type === 'STRING') {
      return String(metric.data);
    }

    return this.translateService.getTranslation(
      'CASE_DETAIL.NO_AVAILABLE_DATA'
    );
  }

  getProgressBarWidthAndColor(metric: SystemResultConfigI) {
    const isPercentage = metric.type == 'PERCENTAGE';

    return this.getProgressBarProperties(metric);
  }

  private getProgressBarProperties(metric: SystemResultConfigI): {
    colorClass: string;
    width: string;
  } {
    let colorClass = 'bg-secondary';
    let width = '0%';

    if (!metric.data && !this.isSeverityMetric(metric)) {
      return { colorClass, width };
    }

    if (this.isSeverityMetric(metric)) {
      return {
        colorClass: this.getSeverityColor(metric.severity),
        width: '100%',
      };
    }

    if (metric.type == 'BOOLEAN') {
      if (metric.data == -1) {
        return { colorClass: 'bg-danger', width: '100%' };
      } else if (metric.data == 0) {
        return { colorClass: 'bg-warning', width: '100%' };
      } else {
        return { colorClass: 'bg-success', width: '100%' };
      }
    } else if (metric.type == 'STRING') {
      const scoreLower = metric.data.toLowerCase();

      switch (scoreLower) {
        case 'regular':
          colorClass = 'bg-success';
          width = '100%';
          break;
        case 'outlier':
          colorClass = 'bg-warning';
          width = '75%';
          break;
        case 'extreme outlier':
          colorClass = 'bg-danger';
          width = '100%';
          break;
      }
      return { colorClass, width };
    } else if (metric.type == 'NUMERIC' || metric.type == 'PERCENTAGE') {
      if (metric.data <= 50) {
        colorClass = 'bg-success';
      } else if (metric.data <= 80) {
        colorClass = 'bg-warning';
      } else {
        colorClass = 'bg-danger';
      }

      width = `${Math.min(Math.max(metric.data, 0), 100)}%`;
    }

    return { colorClass, width };
  }

  private getRuleBasedProfileProperties(dataScore: number): {
    colorClass: string;
    iconClass: string;
  } {
    let colorClass = 'bg-white';
    let iconClass = '';

    if (!dataScore) {
      colorClass = 'bg-secondary';
      iconClass = ' ';
      return { colorClass, iconClass };
    }
    if (dataScore <= 50) {
      colorClass = 'bg-success';
      iconClass = 'text-3xl fa-solid fa-check text-white';
    } else if (dataScore <= 80) {
      colorClass = 'bg-warning';
      iconClass = 'text-3xl fa-solid fa-exclamation text-white';
    } else if (dataScore > 79) {
      colorClass = 'bg-danger';
      iconClass = 'text-3xl fa-solid fa-exclamation-triangle text-white';
    }
    return { colorClass, iconClass };
  }

  private isSeverityMetric(metric: SystemResultConfigI): boolean {
    return !!metric.severity;
  }

  private getSeverityColor(severity?: string | null): string {
    switch (severity) {
      case 'LowSeverity':
        return 'bg-success';
      case 'MediumSeverity':
        return 'bg-warning';
      case 'HighSeverity':
        return 'bg-danger';
      default:
        return 'bg-gray-300';
    }
  }

  private getSeverityIcon(severity?: string | null): string {
    switch (severity) {
      case 'LowSeverity':
        return 'text-3xl fa-solid fa-check text-white';
      case 'MediumSeverity':
        return 'text-3xl fa-solid fa-exclamation text-white';
      case 'HighSeverity':
        return 'text-3xl fa-solid fa-exclamation-triangle text-white';
      default:
        return 'text-3xl fa-solid fa-question text-white';
    }
  }

  getModelTooltip(metric: SystemResultConfigI): string {
    const m = metric.modelMetrics;
    if (!m) return '';

    const parts: string[] = [];

    parts.push(`${m.modelName} (v${m.version})`);

    if (m.accuracy != null)
      parts.push(`Accuracy: ${(m.accuracy * 100).toFixed(1)}%`);
    if (m.precision != null)
      parts.push(`Precision: ${(m.precision * 100).toFixed(1)}%`);
    if (m.recall != null) parts.push(`Recall: ${(m.recall * 100).toFixed(1)}%`);
    if (m.f1Score != null) parts.push(`F1: ${(m.f1Score * 100).toFixed(1)}%`);

    if (m.rocAreaUnderCurve != null) {
      parts.push(`ROC AUC: ${m.rocAreaUnderCurve.toFixed(3)}`);
    }

    if (m.trainingDate) {
      parts.push(`Training: ${m.trainingDate}`);
    }
    if (m.deploymentDate) {
      parts.push(`Deployed: ${m.deploymentDate}`);
    }

    return parts.join('\n');
  }

  getLevelBadgeColor(metric: SystemResultConfigI): string {
    if (this.isTriagingMetric(metric)) {
      const status = this.getTriagingAggregateStatus();

      if (status === 'danger') {
        return 'bg-danger';
      }

      if (status === 'success') {
        return 'bg-success';
      }

      return 'bg-warning';
    }

    return this.getBaseLevelBadgeColor(metric);
  }

  private getBaseLevelBadgeColor(metric: SystemResultConfigI): string {
    const value = Number(metric.data);

    if (metric.severity != null && metric.severity !== '') {
      if (!Number.isFinite(value)) {
        return 'bg-secondary';
      }

      if (value <= 0.4) {
        return 'bg-success';
      } else if (value <= 0.95) {
        return 'bg-warning';
      } else {
        return 'bg-danger';
      }
    }

    switch (value) {
      case 0:
        return 'bg-secondary';
      case 1:
        return 'bg-success';
      case 2:
        return 'bg-warning';
      case 3:
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  getLevelBadgeIcon(metric: SystemResultConfigI): string {
    if (this.isTriagingMetric(metric)) {
      const status = this.getTriagingAggregateStatus();

      if (status === 'danger') {
        return 'fa fa-exclamation-triangle text-white text-2xl';
      }

      if (status === 'success') {
        return 'fa fa-check text-white text-2xl';
      }

      return 'fa fa-exclamation text-white text-2xl';
    }

    return this.getBaseLevelBadgeIcon(metric);
  }

  private getBaseLevelBadgeIcon(metric: SystemResultConfigI): string {
    const value = Number(metric.data);

    if (metric.severity != null && metric.severity !== '') {
      if (!Number.isFinite(value)) {
        return 'fa fa-ban text-white text-2xl';
      }

      if (value <= 0.4) {
        return 'fa fa-check text-white text-2xl';
      } else if (value <= 0.95) {
        return 'fa fa-exclamation text-white text-2xl';
      } else {
        return 'fa fa-exclamation-triangle text-white text-2xl';
      }
    }

    switch (value) {
      case 0:
        return 'fa fa-ban text-white text-2xl';
      case 1:
        return 'fa fa-check text-white text-2xl';
      case 2:
        return 'fa fa-exclamation text-white text-2xl';
      case 3:
        return 'fa fa-exclamation-triangle text-white text-2xl';
      default:
        return 'fa fa-ban text-white text-2xl';
    }
  }

  getBooleanBadgeColor(metric: SystemResultConfigI): string {
    // true/1: green, false/0: red
    if (metric.data === true || metric.data === 1 || metric.data === 'true') {
      return 'bg-success';
    } else if (
      metric.data === false ||
      metric.data === 0 ||
      metric.data === 'false'
    ) {
      return 'bg-danger';
    }
    return 'bg-secondary';
  }

  getBooleanBadgeIcon(metric: SystemResultConfigI): string {
    if (metric.data === true || metric.data === 1 || metric.data === 'true') {
      return 'fa fa-check text-white text-2xl';
    } else if (
      metric.data === false ||
      metric.data === 0 ||
      metric.data === 'false'
    ) {
      return 'fa fa-exclamation-triangle text-white text-2xl';
    }
    return 'fa fa-ban text-white text-2xl';
  }

  getInfoBadgeColor(metric: SystemResultConfigI): string {
    return 'bg-secondary';
  }

  getInfoBadgeIcon(metric: SystemResultConfigI): string {
    return 'fa fa-info text-white text-2xl';
  }

  getTriagingAggregateStatus(): 'danger' | 'success' | 'warning' {
    const metricsForTriaging = this.data().filter(
      (metric) => !this.isTriagingMetric(metric)
    );

    const statusEntries = metricsForTriaging.map((metric) => ({
      label: metric.label,
      type: metric.type,
      data: metric.data,
      status: this.getMetricStatusForTriaging(metric),
    }));

    const statuses = statusEntries
      .map((entry) => entry.status)
      .filter((status): status is 'red' | 'orange' | 'green' =>
        status === 'red' || status === 'orange' || status === 'green'
      );

    const hitCount = statuses.filter((status) => status === 'red').length;

    if (hitCount >= 2) {
      return 'danger';
    }

    if (statuses.length > 0 && statuses.every((status) => status === 'green')) {
      return 'success';
    }

    return 'warning';
  }

  private isTriagingMetric(metric: SystemResultConfigI): boolean {
    if (metric.type !== 'LEVEL_BADGE') {
      return false;
    }

    const triagingLabel = this.translateService.getTranslation(
      'CASE_DETAIL.TRIAGING_INDEX'
    );

    const normalizedLabel = String(metric.label ?? '').trim().toLowerCase();
    const normalizedTriagingLabel = String(triagingLabel ?? '').trim().toLowerCase();

    return (
      normalizedLabel === normalizedTriagingLabel ||
      metric.modelMetrics?.modelName === 'SeverityIndex'
    );
  }

  private getMetricStatusForTriaging(
    metric: SystemResultConfigI
  ): 'red' | 'orange' | 'green' | 'neutral' {
    if (metric.type === 'INFO_BADGE') {
      return 'neutral';
    }

    const colorClass =
      metric.type === 'LEVEL_BADGE'
        ? this.getBaseLevelBadgeColor(metric)
        : metric.type === 'BOOLEAN_BADGE'
          ? this.getBooleanBadgeColor(metric)
          : this.getBackgroundColor(metric);

    if (colorClass === 'bg-danger') {
      return 'red';
    }

    if (colorClass === 'bg-warning') {
      return 'orange';
    }

    if (colorClass === 'bg-success') {
      return 'green';
    }

    return 'neutral';
  }

  getSubcategoryBadgeColor(metric: SystemResultConfigI): string {
    const badgeMetric = this.toSubcategoryBadgeMetric(metric);

    if (!badgeMetric) {
      return 'bg-secondary';
    }

    if (badgeMetric.type === 'LEVEL_BADGE') {
      return this.getLevelBadgeColor(badgeMetric);
    }

    if (badgeMetric.type === 'INFO_BADGE') {
      return this.getInfoBadgeColor(badgeMetric);
    }

    return this.getBooleanBadgeColor(badgeMetric);
  }

  getSubcategoryBadgeIcon(metric: SystemResultConfigI): string {
    const badgeMetric = this.toSubcategoryBadgeMetric(metric);

    if (!badgeMetric) {
      return 'fa fa-ban text-white text-base';
    }

    if (badgeMetric.type === 'LEVEL_BADGE') {
      return this.getLevelBadgeIcon(badgeMetric).replace('text-2xl', 'text-base');
    }

    if (badgeMetric.type === 'INFO_BADGE') {
      return this.getInfoBadgeIcon(badgeMetric).replace('text-2xl', 'text-base');
    }

    return this.getBooleanBadgeIcon(badgeMetric).replace('text-2xl', 'text-base');
  }

  private toSubcategoryBadgeMetric(
    metric: SystemResultConfigI
  ): SystemResultConfigI | null {
    const badge = metric.subcategory?.badge;

    if (!badge) {
      return null;
    }

    return {
      ...metric,
      data: badge.data,
      type: badge.type ?? 'BOOLEAN_BADGE',
      severity: undefined,
    };
  }
}
